import React, { useState, useRef, useEffect } from 'react';
import {
  Paper,
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Send, Paperclip } from 'lucide-react';
import { Message, Conversation, MessageAttachment } from '../../../types';
import { AttachmentDialog } from '../AttachmentDialog';
import { ThreadHeader } from './ThreadHeader';
import { MessageList } from './MessageList';
import { PendingAttachments } from './PendingAttachments';

interface MessageThreadProps {
  conversation?: Conversation;
  messages: Message[];
  onSendMessage: (message: Omit<Message, 'id' | 'timestamp' | 'read'>) => void;
}

export const MessageThread: React.FC<MessageThreadProps> = ({
  conversation,
  messages,
  onSendMessage,
}) => {
  const [newMessage, setNewMessage] = useState('');
  const [attachmentDialogOpen, setAttachmentDialogOpen] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState<MessageAttachment[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<number>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const scrollToBottom = () => {
    if (scrollTimeoutRef.current) {
      window.clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = window.setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  useEffect(() => {
    scrollToBottom();
    
    return () => {
      if (scrollTimeoutRef.current) {
        window.clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [messages]);

  const handleSend = () => {
    if (!conversation || (!newMessage.trim() && pendingAttachments.length === 0)) return;

    const recipientId = conversation.participants.find(id => id !== '1'); // Current user ID
    if (!recipientId) return;

    onSendMessage({
      conversationId: conversation.id,
      senderId: '1', // Current user ID
      recipientId,
      content: newMessage.trim(),
      caseId: conversation.caseId,
      attachments: pendingAttachments.length > 0 ? pendingAttachments : undefined,
    });

    setNewMessage('');
    setPendingAttachments([]);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleAttach = (files: File[], caseId?: string, stageId?: string) => {
    const newAttachments: MessageAttachment[] = files.map(file => ({
      id: `att-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: file.name,
      type: file.type,
      url: URL.createObjectURL(file),
      size: file.size,
      uploadDate: new Date().toISOString(),
      caseId,
      stageId,
    }));

    setPendingAttachments(prev => [...prev, ...newAttachments]);
  };

  const removePendingAttachment = (attachmentId: string) => {
    setPendingAttachments(prev => prev.filter(att => att.id !== attachmentId));
  };

  if (!conversation) {
    return (
      <Paper 
        sx={{ 
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography color="text.secondary">
          Select a conversation to start messaging
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper 
      sx={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      <ThreadHeader conversation={conversation} />

      <MessageList
        messages={messages}
        messagesEndRef={messagesEndRef}
      />

      <Box 
        sx={{ 
          p: 2,
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          position: 'sticky',
          bottom: 0,
          zIndex: 1,
        }}
      >
        {pendingAttachments.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <PendingAttachments
              attachments={pendingAttachments}
              onRemove={removePendingAttachment}
            />
          </Box>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            placeholder="Type a message..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          />
          
          <Box sx={{ 
            display: 'flex', 
            gap: 1,
            justifyContent: 'flex-end',
          }}>
            <Tooltip title="Attach files">
              <IconButton 
                color="primary"
                onClick={() => setAttachmentDialogOpen(true)}
              >
                <Paperclip size={20} />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              onClick={handleSend}
              disabled={!newMessage.trim() && pendingAttachments.length === 0}
              startIcon={<Send size={18} />}
            >
              Send
            </Button>
          </Box>
        </Box>
      </Box>

      <AttachmentDialog
        open={attachmentDialogOpen}
        onClose={() => setAttachmentDialogOpen(false)}
        onAttach={handleAttach}
        currentCaseId={conversation.caseId}
      />
    </Paper>
  );
};